import * as React from 'react';
import { RefreshCw, Trash2, UserX, UserCheck, KeyRound, X, Coins } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { listUsers, toggleUser, deleteUser, resetUserPassword, adjustUserCredit } from '@/api/client';

type ManagedUser = {
  id: number;
  username: string;
  display_name: string;
  role: string;
  status: string;
  permissions: any;
  credit_balance: number;
  last_login_at: string | null;
  created_at: string;
};

export function UsersTab() {
  const [users, setUsers] = React.useState<ManagedUser[]>([]);
  const [loading, setLoading] = React.useState(true);

  // 重置密码弹窗状态
  const [resetTarget, setResetTarget] = React.useState<ManagedUser | null>(null);
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [resetMsg, setResetMsg] = React.useState('');
  const [resetError, setResetError] = React.useState('');
  const [resetting, setResetting] = React.useState(false);

  // 调整积分弹窗状态
  const [creditTarget, setCreditTarget] = React.useState<ManagedUser | null>(null);
  const [creditAmount, setCreditAmount] = React.useState('');
  const [creditReason, setCreditReason] = React.useState('');
  const [creditError, setCreditError] = React.useState('');

  // 通用确认弹窗状态（删除/禁用/启用/调整积分共用，替代浏览器原生 confirm）
  const [confirmState, setConfirmState] = React.useState<{
    title: string;
    message: string;
    confirmText: string;
    danger?: boolean;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState('');

  const closeConfirm = React.useCallback(() => {
    if (confirmBusy) return;
    setConfirmState(null);
    setConfirmError('');
  }, [confirmBusy]);

  const runConfirm = React.useCallback(async () => {
    if (!confirmState) return;
    setConfirmBusy(true);
    setConfirmError('');
    try {
      await confirmState.onConfirm();
      setConfirmState(null);
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : '操作失败');
    } finally {
      setConfirmBusy(false);
    }
  }, [confirmState]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data.users || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleToggle = React.useCallback((user: ManagedUser) => {
    const disabled = user.status !== 'disabled';
    setConfirmState({
      title: disabled ? '禁用用户' : '启用用户',
      message: `确定要${disabled ? '禁用' : '启用'}用户 ${user.username} 吗？`,
      confirmText: disabled ? '禁用' : '启用',
      danger: disabled,
      onConfirm: async () => {
        await toggleUser(user.username, disabled);
        load();
      },
    });
  }, [load]);

  const handleDelete = React.useCallback((user: ManagedUser) => {
    setConfirmState({
      title: '删除用户',
      message: `确定要删除用户 ${user.username} 吗？此操作不可恢复，其全部数据（内容库、积分流水、监控账号等）将一并删除！`,
      confirmText: '删除',
      danger: true,
      onConfirm: async () => {
        await deleteUser(user.username);
        load();
      },
    });
  }, [load]);

  const openReset = React.useCallback((user: ManagedUser) => {
    setResetTarget(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetMsg('');
    setResetError('');
  }, []);

  const closeReset = React.useCallback(() => {
    if (resetting) return;
    setResetTarget(null);
  }, [resetting]);

  const handleResetSubmit = React.useCallback(async () => {
    if (!resetTarget) return;
    setResetMsg('');
    setResetError('');
    if (newPassword.length < 6) {
      setResetError('密码至少 6 位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('两次输入的密码不一致');
      return;
    }
    setResetting(true);
    try {
      const res = await resetUserPassword(resetTarget.username, newPassword);
      setResetMsg(res.message || '密码已重置');
      setTimeout(() => setResetTarget(null), 1200);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : '重置失败');
    } finally {
      setResetting(false);
    }
  }, [resetTarget, newPassword, confirmPassword]);

  const openCredit = React.useCallback((user: ManagedUser) => {
    setCreditTarget(user);
    setCreditAmount('');
    setCreditReason('');
    setCreditError('');
  }, []);

  const closeCredit = React.useCallback(() => {
    setCreditTarget(null);
  }, []);

  const handleCreditSubmit = React.useCallback(() => {
    if (!creditTarget) return;
    setCreditError('');
    const amt = parseFloat(creditAmount);
    if (isNaN(amt) || amt === 0) {
      setCreditError('请输入非 0 的积分数量（正数充值 / 负数扣减）');
      return;
    }
    setConfirmState({
      title: '调整积分',
      message: `确定为 ${creditTarget.username} ${amt > 0 ? '充值' : '扣减'} ${Math.abs(amt)} 积分？`,
      confirmText: '确认调整',
      danger: amt < 0,
      onConfirm: async () => {
        await adjustUserCredit(creditTarget.username, amt, creditReason);
        setCreditTarget(null);
        load();
      },
    });
  }, [creditTarget, creditAmount, creditReason, load]);

  const canManage = (user: ManagedUser) => user.role !== 'admin';

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>用户管理</CardTitle>
            <CardDescription>查看和管理系统用户</CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={load} title="刷新">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">用户名</th>
                <th className="px-4 py-3 text-left font-medium">显示名</th>
                <th className="px-4 py-3 text-left font-medium">角色</th>
                <th className="px-4 py-3 text-left font-medium">状态</th>
                <th className="px-4 py-3 text-left font-medium">积分</th>
                <th className="px-4 py-3 text-left font-medium">最后登录</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="p-4" colSpan={7}><Skeleton className="h-8 w-full" /></td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-center text-muted-foreground" colSpan={7}>
                    暂无用户
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">{user.username}</td>
                    <td className="px-4 py-3">{user.display_name || '—'}</td>
                    <td className="px-4 py-3">
                      {user.role === 'admin' ? (
                        <Badge variant="default">管理员</Badge>
                      ) : (
                        <Badge variant="secondary">用户</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.status === 'disabled' ? (
                        <Badge variant="destructive">已禁用</Badge>
                      ) : (
                        <Badge variant="success">正常</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 font-medium tabular-nums">
                        <Coins className="h-3.5 w-3.5 text-amber-500" />
                        {(user.credit_balance ?? 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {user.last_login_at ? new Date(user.last_login_at).toLocaleString('zh-CN') : '从未登录'}
                    </td>
                    <td className="px-4 py-3">
                      {canManage(user) && (
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => openCredit(user)}
                            title="调整积分"
                          >
                            <Coins className="h-3.5 w-3.5 text-amber-500" />
                            调整积分
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => openReset(user)}
                            title="重置密码"
                          >
                            <KeyRound className="h-3.5 w-3.5" />
                            重置密码
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => handleToggle(user)}
                          >
                            {user.status === 'disabled' ? (
                              <><UserCheck className="h-3.5 w-3.5" />启用</>
                            ) : (
                              <><UserX className="h-3.5 w-3.5" />禁用</>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-xs text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(user)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {/* 重置密码弹窗 */}
    {resetTarget && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={closeReset}
      >
        <div
          className="w-full max-w-md rounded-xl border bg-background p-6 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold tracking-tight">重置密码</h3>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={closeReset}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            为 <span className="font-medium text-foreground">{resetTarget.username}</span>
            {resetTarget.display_name ? `（${resetTarget.display_name}）` : ''} 设置新密码
          </p>

          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-sm font-medium">新密码</Label>
              <Input
                type="password"
                className="mt-1.5"
                placeholder="至少 6 位"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">确认新密码</Label>
              <Input
                type="password"
                className="mt-1.5"
                placeholder="再次输入新密码"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {resetError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {resetError}
              </div>
            )}
            {resetMsg && (
              <div className="rounded-md border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600">
                {resetMsg}
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleResetSubmit}
              disabled={resetting}
            >
              {resetting ? '重置中…' : '确认重置'}
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* 调整积分弹窗 */}
    {(() => {
      const target = creditTarget;
      if (!target) return null;
      return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={closeCredit}
      >
        <div
          className="w-full max-w-md rounded-xl border bg-background p-6 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold tracking-tight">调整积分</h3>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={closeCredit}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            为 <span className="font-medium text-foreground">{target.username}</span>
            {target.display_name ? `（${target.display_name}）` : ''} 调整积分
            （当前余额 <span className="font-medium text-foreground">{(target.credit_balance ?? 0).toFixed(2)}</span>）
          </p>

          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-sm font-medium">积分数量</Label>
              <Input
                type="number"
                className="mt-1.5"
                placeholder="正数充值 / 负数扣减，如 50 或 -20"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">备注（可选）</Label>
              <Input
                className="mt-1.5"
                placeholder="如：积分不足补偿 / 体验赠送"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
              />
            </div>

            {creditError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {creditError}
              </div>
            )}

            <Button className="w-full" onClick={handleCreditSubmit}>
              确认调整
            </Button>
          </div>
        </div>
      </div>
      );
    })()}

    {/* 通用确认弹窗（删除/禁用/启用/调整积分） */}
    {confirmState && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={closeConfirm}
      >
        <div
          className="w-full max-w-md rounded-xl border bg-background p-6 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold tracking-tight">{confirmState.title}</h3>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={closeConfirm}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{confirmState.message}</p>

          {confirmError && (
            <div className="mt-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {confirmError}
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={closeConfirm} disabled={confirmBusy}>
              取消
            </Button>
            <Button
              variant={confirmState.danger ? 'destructive' : 'default'}
              onClick={runConfirm}
              disabled={confirmBusy}
            >
              {confirmBusy ? '处理中…' : confirmState.confirmText}
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

